import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Tag as UIAPITag } from '@customerio/hydra-ui-api';
import { hydraUiApi } from '@/services/api';
import type { HookMutationConfig } from '../shared/react-query-types';
import { tagKeys } from './entity';

/**
 * Tag writes.
 *
 * Creating is the only one the app does: a tag is named once and then
 * attached by id, and the attaching is owned by whatever it is attached to
 * (a campaign, an attribute, an event), not by the tag.
 */

export type CreateTagInput = {
  workspaceId: number;
  name: string;
};

/**
 * Name a new tag. The response carries the tag the server made, which is the
 * only way to learn the id the caller needs to attach it.
 *
 * A name the workspace already uses is refused with a conflict rather than
 * silently returning the existing tag, so the caller decides what to say.
 *
 * @public
 */
export async function createTag({ workspaceId, name }: CreateTagInput): Promise<UIAPITag> {
  const raw = await hydraUiApi.tags.create({
    params: { path: { environment_id: String(workspaceId) } },
    body: { tag: { name } },
  });

  if (raw.tag == null) {
    throw new Error('missing tag in create tag response');
  }
  return raw.tag;
}

export type UseCreateTagVariables = Omit<CreateTagInput, 'workspaceId'>;

type CreateTagOptions = {
  workspaceId: number;
  mutationConfig?: HookMutationConfig<UIAPITag, UseCreateTagVariables>;
};

/** @public */
export const useCreateTag = ({ workspaceId, mutationConfig }: CreateTagOptions) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = mutationConfig ?? {};

  return useMutation({
    mutationFn: (input: UseCreateTagVariables) => createTag({ ...input, workspaceId }),
    onSuccess: (result, ...args) => {
      void queryClient.invalidateQueries({ queryKey: tagKeys.lists(workspaceId) });
      onSuccess?.(result, ...args);
    },
    ...rest,
  });
};
